/**
 * Development logger for API requests and responses
 * Only logs in development mode or when explicitly enabled
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isLoggingEnabled = process.env.REACT_APP_API_LOGGING === 'true' || isDevelopment;

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
    [key: string]: any;
}

const truncate = (value: any, maxLength: number = 200): any => {
    if (typeof value === 'string') {
        return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
    }
    if (typeof value === 'object' && value !== null) {
        const str = JSON.stringify(value);
        return str.length > maxLength ? str.substring(0, maxLength) + '...' : value;
    }
    return value;
};

class Logger {
    private enabled: boolean;

    constructor() {
        this.enabled = isLoggingEnabled;
    }

    private log(level: LogLevel, message: string, context?: LogContext) {
        if (!this.enabled) return;

        const timestamp = new Date().toISOString();
        const prefix = `[API ${level.toUpperCase()}]`;

        const logData = {
            timestamp,
            message,
            ...context,
        };

        switch (level) {
            case 'debug':
                console.debug(prefix, logData);
                break;
            case 'info':
                console.info(prefix, logData);
                break;
            case 'warn':
                console.warn(prefix, logData);
                break;
            case 'error':
                console.error(prefix, logData);
                break;
        }
    }

    debug(message: string, context?: LogContext) {
        this.log('debug', message, context);
    }

    info(message: string, context?: LogContext) {
        this.log('info', message, context);
    }

    warn(message: string, context?: LogContext) {
        this.log('warn', message, context);
    }

    error(message: string, context?: LogContext) {
        this.log('error', message, context);
    }

    logRequest(method: string, url: string, options?: RequestInit) {
        if (!this.enabled) return;

        this.info('Request', {
            method,
            url,
            headers: options?.headers,
            body: options?.body ? truncate(options.body) : undefined,
        });
    }

    logResponse(
        method: string,
        url: string,
        status: number,
        statusText: string,
        duration: number,
        cached: boolean = false
    ) {
        if (!this.enabled) return;

        this.info('Response', {
            method,
            url,
            status,
            statusText,
            duration: `${duration}ms`,
            cached,
        });
    }

    logError(
        method: string,
        url: string,
        error: Error,
        retryCount?: number
    ) {
        if (!this.enabled) return;

        this.error('Request Failed', {
            method,
            url,
            error: error.message,
            retryCount,
        });
    }

    logCacheHit(key: string) {
        this.debug('Cache Hit', { key });
    }

    logCacheMiss(key: string) {
        this.debug('Cache Miss', { key });
    }

    logCacheInvalidation(pattern: string) {
        this.info('Cache Invalidated', { pattern });
    }
}

export const logger = new Logger();
export { truncate };
